import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FilterAttributeRangeComponentDef } from '@df/catalog-ui/listing/filter/filter-attribute-range.component.def';
import { TimeoutService } from '@df/ng/timeout.service';
import { RangeSliderChangeContext } from '@df/ui/form/range-slider/change-context';
import { RangeSliderOptions } from '@df/ui/form/range-slider/options';
import { RangeSliderModule } from '@df/ui/form/range-slider/range-slider.module';
import { PriceComponent } from '@df/ui/price/price.component';
import { PriceService } from '@df/ui/price/price.service';
import max from 'lodash-es/max';
import min from 'lodash-es/min';
import { Product } from '../../../catalog/product/product.decorator';
import { FilterExpandableComponent } from './filter-expandable.component';
export interface IPriceOption {
    min: number;
    max?: number;
}
@Component({
    selector: 'filter-attribute-range',
    templateUrl: './filter-attribute-range.html',
    styleUrls: ['./filter-attribute-range.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [PriceComponent, FilterExpandableComponent, RangeSliderModule, CommonModule]
})
export class FilterAttributeRangeComponent<T extends Product> extends FilterAttributeRangeComponentDef<T> implements OnInit, OnChanges {
    zippyId() {
        return ['filter', this.filter.id];
    }
    @Input()
    isDefault = false;
    protected _rangeMin?: number;
    protected _rangeMax?: number;
    protected _min?: number;
    protected _max?: number;
    set rangeMin(value: number) {
        this._rangeMin = value;
        this._min = value;
    }
    get rangeMin() {
        return this._rangeMin || 0;
    }
    set rangeMax(value: number) {
        this._rangeMax = value;
        this._max = value;
    }
    get rangeMax() {
        return this._rangeMax || 0;
    }
    set min(value: number) {
        if (value !== this._min) {
            this._min = value;
        }
    }
    get min() {
        return this._min || 0;
    }
    set max(value: number | undefined) {
        if (value !== this._max) {
            this._max = value;
        }
    }
    get max() {
        return this._max || 0;
    }
    options: RangeSliderOptions = new RangeSliderOptions();
    constructor(
        changeDetectorRef: ChangeDetectorRef,
        timeoutService: TimeoutService,
        protected priceService: PriceService
    ) {
        super(changeDetectorRef, timeoutService);
    }
    override ngOnInit() {
        this.rangeMin = this.metaMin;
        this.rangeMax = this.metaMax;
        if (this.filter.hasSelection) {
            if (this.filter.selection?.min) {
                this.rangeMin = Math.max(this.metaMin, this.filter.selection.min);
                this.filter.min = this.rangeMin;
            }
            if (this.filter.selection?.max) {
                this.rangeMax = Math.min(this.metaMax, this.filter.selection.max);
                this.filter.max = this.rangeMax;
            }
        }
    }
    override ngOnChanges(changes: SimpleChanges) {
        if (!changes['options']) {
            this.updateOptions();
        }
        super.ngOnChanges(changes);
    }
    override get metaMax(): number {
        return this.filter?.getMetaData()?.max;
    }
    override get metaMin(): number {
        return this.filter?.getMetaData()?.min;
    }
    get noRangePriceForResults(): boolean {
        return Math.abs((this.getMaxPrice() || 0) - (this.getMinPrice() || 0)) < 0.001;
    }
    onUserChange(event: RangeSliderChangeContext) {
        this.rangeMin = this.min = event.value;
        this.rangeMax = this.max = event.highValue || 0;
        this.detectChanges();
    }
    onUserChangeEnd(event: RangeSliderChangeContext) {
        if (this.filter) {
            this.filter.min = event.value;
            this.filter.max = event.highValue;
        }
    }
    getMinPrice() {
        const models = this.filter.listing.getFilteredModels();
        return min(models.map(i => i['price']).filter(i => !!i));
    }
    getMaxPrice() {
        const models = this.filter.listing.getFilteredModels();
        return max(models.map(i => i['price']).filter(i => !!i));
    }
    protected updateOptions() {
        this.options = {
            floor: this.metaMin,
            ceil: this.metaMax,
            step: 1,
            hideLimitLabels: true,
            hidePointerLabels: true,
            outputEventsInterval: 50,
            translate: (value: number): string => {
                return this.priceService.toPlainString(value) || '';
            }
        };
    }
}
